import {RoomClientState} from '../../../../shared/scripts/RoomClientState.ts'
import {LobbyImage, UserSessionId} from '@colonist/types'
import {UIRoomExitConfirmationViewPopup} from '../../../../shared/scripts/ui_html/room/UIRoomExitConfirmationViewPopup'
import {UIMRoomPageController} from './UIMRoomPageController'
import {UIMRoomEditPageController} from './UIMRoomEditPageController'
import {UIMRoomSetupPageController} from './UIMRoomSetupPageController'
import {SocketLobbySend} from '../../../../shared/scripts/socket/SocketLobbySend'
import {RoomType} from '@colonist/lobby-types'
import {UIUrlController} from '../../../../shared/scripts/ui_html/UIUrlController'
import {notificationController, socket} from '../../../../shared/scripts/EntryShared'
import {UIOnlineFriendController} from '../../../../shared/scripts/ui_friends/UIOnlineFriendController'
import {UIRoomSettingsController} from '../../../../shared/scripts/ui_room/UIRoomSettingsController'
import {RoomStateChangerNotifier} from '../../../../shared/scripts/RoomStateChangerNotifier.ts'
import {clientI18n} from '../../../../shared/scripts/ClientI18n.ts'

export enum RoomStep {
    RoomPage,
    EditRoom,
    SetupRoom
}

type UIMRoomStepControllers = UIMRoomSetupPageController | UIMRoomEditPageController | UIMRoomPageController

export class UIMCreateRoomController {
    private roomState: RoomClientState

    private roomSetupController: UIMRoomSetupPageController | undefined
    private roomEditPageController: UIMRoomEditPageController | undefined
    private roomPageController: UIMRoomPageController | undefined

    private pageOnNextUpdate: RoomStep | undefined = undefined

    init() {
        socket.controller.registerSessionEstablishedObserver(() => {
            if(this.isUserOnCreateRoomFlow()) {
                this.handleSocketReconnection()
            }
        })
    }

    updateRoomState(roomState: RoomClientState) {
        const hadRoom = this.roomState != undefined
        const isSameRoom = roomState.gameSetting.id == this.roomState?.gameSetting.id
        const isReceivedRoomStateOlder = isSameRoom && (roomState.updateSequence < this.roomState.updateSequence)
        if(isReceivedRoomStateOlder) return

        RoomStateChangerNotifier.run(this.roomState, roomState)

        this.roomState = roomState

        this.updateAllControllersWithRoomState()
        this.initNextStepForRoomCreationFlow(!isSameRoom && hadRoom)
        this.showPageOnNextUpdate()
    }

    onKickedInactiveHost() {
        if(this.roomState.isHost(socket.controller.session.id)) {
            const text = clientI18n.t('strings:roomPage.notifications.kickedDueToInactivity')
            this.receivedChatSystemMessage(text)
        }
        notificationController.showNotification(LobbyImage.Lightning, {key: 'strings:roomPage.notifications.kickingInactiveHost'})
    }

    private updateAllControllersWithRoomState() {
        this.roomSetupController?.update(this.roomState)
        this.roomEditPageController?.update(this.roomState)
        this.roomPageController?.update(this.roomState)
    }

    private initNextStepForRoomCreationFlow(forceRoomPage: boolean) {
        const userOnCreateRoomFlow = (this.pageOnNextUpdate == undefined) || forceRoomPage
        if(userOnCreateRoomFlow) this.pageOnNextUpdate = RoomStep.RoomPage
    }

    private showPageOnNextUpdate(activeTabIndex?: number) {
        window.history.replaceState(null, '', '#' + this.roomState.gameSetting.id)

        switch(this.pageOnNextUpdate) {
            case RoomStep.SetupRoom: return this.showSetupScreen(activeTabIndex)
            case RoomStep.EditRoom: return this.showEditRoomScreen(activeTabIndex)
            case RoomStep.RoomPage: return this.showActiveRoomScreen(activeTabIndex)
            default: return
        }
    }

    private showActiveRoomScreen(tabIndex?: number) {
        if(this.roomPageController == undefined) this.roomPageController = new UIMRoomPageController(this.roomState)
        UIOnlineFriendController.userEnteredRoom()
        this.showRoomStep(this.roomPageController)
        this.roomPageController.showView(tabIndex)
    }

    private showEditRoomScreen(tabIndex?: number) {
        if(this.roomEditPageController == undefined) this.roomEditPageController = new UIMRoomEditPageController(this.roomState)
        this.showRoomStep(this.roomEditPageController)
        this.roomEditPageController.showView(tabIndex)
    }

    private showSetupScreen(tabIndex?: number) {
        if(this.roomSetupController == undefined) this.roomSetupController = new UIMRoomSetupPageController(this.roomState)
        this.showRoomStep(this.roomSetupController)
        this.roomSetupController.showView(tabIndex)
    }

    receivedChatMessage(message: string, senderSessionId: UserSessionId) {
        const session = this.roomState.getSessions().find((aSession) => aSession.userSessionId == senderSessionId)
        this.roomPageController?.receivedChatMessage(session?.icon ?? LobbyImage.IconColonistHat, message, session?.username)
    }

    receivedChatSystemMessage(message: string) {
        this.roomPageController?.receivedChatMessage(LobbyImage.Lightning, message)
    }

    setShowPageOnNextUpdate(page: RoomStep, forceUpdate: boolean = false, activeTabIndex?: number) {
        this.pageOnNextUpdate = page
        if(forceUpdate) this.showPageOnNextUpdate(activeTabIndex)
    }

    initCreateRoomView() {
        this.pageOnNextUpdate = RoomStep.SetupRoom
        SocketLobbySend.clickedCreateRoom(RoomType.Standard)
        setTimeout(() => {
            socket.roomClient.setPrivateGame(true)
            UIRoomSettingsController.initRoomSettingFromLocalStorage()
        }, 500)
    }

    onExitRoom(callback?: () => void, forceExit?: boolean) {
        if(this.pageOnNextUpdate == RoomStep.EditRoom && !forceExit) {
            this.pageOnNextUpdate = RoomStep.RoomPage
            this.roomPageController?.showView()
            return this.roomEditPageController?.exitView()
        }

        new UIRoomExitConfirmationViewPopup(() => {
            this.leaveRoom()
            this.roomSetupController?.roomSettingsController.setPrivateRoomStateSync(false)
            UIOnlineFriendController.userOnlineAndIdle()
            if(callback) callback()
        })
    }

    showRoomStep(roomController: UIMRoomStepControllers) {
        if(this.roomPageController != roomController) this.roomPageController?.hideView()
        if(this.roomSetupController != roomController) this.roomSetupController?.hideView()
        if(this.roomEditPageController != roomController) this.roomEditPageController?.hideView()
    }

    private clearRoom() {
        this.roomPageController?.hideView()
        this.roomSetupController?.exitView()
        this.roomEditPageController?.exitView()
        this.pageOnNextUpdate = undefined;
        (this.roomState as any) = undefined
    }

    leaveRoom() {
        window.location.hash = ''
        this.clearRoom()
        socket.roomClient.leave()
    }

    isUserOnCreateRoomFlow() {
        return this.pageOnNextUpdate != undefined
    }

    // eslint-disable-next-line class-methods-use-this
    disableChatInput() {
        const chatInput = document.querySelector('input.m-room-players-chat-input') as HTMLInputElement | undefined
        if(chatInput) chatInput.disabled = true
    }

    isCreateRoomButNotAHost() {
        if(!this.isUserOnCreateRoomFlow()) return false
        return !this.roomState.isHost(socket.controller.session.id)
    }

    private handleSocketReconnection() {
        this.clearRoom()
        UIUrlController.accessGameFromLink(async(roomId) => {
            await SocketLobbySend.accessGameFromLink(roomId)
        })
    }
}
